import React, { type ReactElement, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import { pluralise } from 'helpers/string.helpers';
import { MarkingPath } from 'pages/marking/routes';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { usePatchGroup, usePostGroup } from 'services/assist-api/entities/group/groups';
import type { PatchGroup, PostGroup } from 'services/assist-api/entities/group/types';
import { type GroupWithGroupUsers } from 'services/assist-api/entities/group/types';
import { useCreateGroupUsers } from 'services/assist-api/entities/groups-users/create';
import type { CreateGroupUsersVariables } from 'services/assist-api/entities/groups-users/create';
import { useDeleteGroupUsers } from 'services/assist-api/entities/groups-users/groups-users';
import { goBack } from 'utils/goBack';
import Description from './description';
import GroupMembers from './group-members';
import {
  getGroupUserIdsToDelete,
  getGroupUsersToPost,
  loadDefaultValues,
  markingGroupFieldsToSave
} from './marking-group-form.helpers';
import {
  type GroupMemberFormValue,
  GroupMemberType,
  type MarkingGroupFormValues,
  markingGroupFormSchema
} from './marking-group-form.schema';
import Name from './name';
import SaveButton from './save-button';

type Props = {
  currentOrganisationId: number;
  markerOrganisationUsers: BaseCustomerUser[];
  nonMarkerOrganisationUsers: BaseCustomerUser[];
  markingGroup?: GroupWithGroupUsers;
};

export default function MarkingGroupForm({
  currentOrganisationId,
  markerOrganisationUsers,
  nonMarkerOrganisationUsers,
  markingGroup
}: Props): ReactElement {
  const defaultValues: MarkingGroupFormValues = loadDefaultValues(
    markerOrganisationUsers,
    nonMarkerOrganisationUsers,
    markingGroup
  );

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(markingGroupFormSchema),
    defaultValues
  });
  const { handleSubmit, watch } = methods;

  const deleteGroupUsers = useDeleteGroupUsers({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to remove ${rejected} ${pluralise('group member', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Removed ${fulfilled} ${pluralise('group member', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to remove group members: ${error.message}`);
    }
  });

  const postGroupUsers = useCreateGroupUsers({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to add ${rejected} new ${pluralise('group member', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Added ${fulfilled} new ${pluralise('group member', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to add group members: ${error.message}`);
    }
  });

  const patchGroup = usePatchGroup({
    onSuccess: () => {
      toast.success('Updated marking group.');
    },
    onError: (error) => {
      toast.error(`Failed to update marking group: ${error.message}`);
    }
  });

  const watchedMarkerGroupMembers: GroupMemberFormValue[] = watch('markerGroupMembers');
  const postGroup = usePostGroup({
    onSuccess: async (responseData) => {
      toast.success('Created marking group.');
      const newGroupId = responseData.data.json.id;
      const groupUsersToPost: CreateGroupUsersVariables = getGroupUsersToPost(
        watchedMarkerGroupMembers,
        newGroupId
      );
      if (groupUsersToPost.customerUsers.length > 0) {
        await postGroupUsers.mutateAsync(groupUsersToPost);
      }
    },
    onError: (error) => {
      toast.error(`Failed to create marking group: ${error.message}`);
    }
  });

  const navigate = useNavigate();
  const redirectToMarkingGroupsTab = useCallback(() => {
    // Attempt to navigate the previous URL (so that we preserve Marking Groups table filters).
    goBack(
      () => navigate(-1),
      () => navigate(MarkingPath.MarkingGroups)
    );
  }, [navigate]);

  /**
   * Updates a marking group, also creating any group users that have been checked and deleting
   * any that have been unchecked.
   */
  const updateMarkingGroup = useCallback(
    async (formValues: MarkingGroupFormValues) => {
      if (markingGroup) {
        const groupToPatch: PatchGroup = {
          id: markingGroup.id,
          data: markingGroupFieldsToSave(formValues)
        };
        const groupUserIdsToDelete: number[] = getGroupUserIdsToDelete(
          formValues.markerGroupMembers,
          formValues.nonMarkerGroupMembers
        );
        const groupUsersToPost: CreateGroupUsersVariables = getGroupUsersToPost(
          formValues.markerGroupMembers,
          markingGroup.id
        );
        await patchGroup.mutateAsync(groupToPatch);
        if (groupUserIdsToDelete.length > 0) {
          await deleteGroupUsers.mutateAsync(groupUserIdsToDelete);
        }
        if (groupUsersToPost.customerUsers.length > 0) {
          await postGroupUsers.mutateAsync(groupUsersToPost);
        }
        redirectToMarkingGroupsTab();
      }
    },
    [deleteGroupUsers, markingGroup, patchGroup, postGroupUsers, redirectToMarkingGroupsTab]
  );

  /**
   * Creates a marking group, also creating any group users that have been checked.
   */
  const createMarkingGroup = useCallback(
    async (formValues: MarkingGroupFormValues) => {
      const groupToPost: PostGroup = {
        ...markingGroupFieldsToSave(formValues),
        organisationId: currentOrganisationId
      };
      await postGroup.mutateAsync(groupToPost);
      redirectToMarkingGroupsTab();
    },
    [currentOrganisationId, postGroup, redirectToMarkingGroupsTab]
  );

  const onSubmit: SubmitHandler<MarkingGroupFormValues> = useCallback(
    (formValues): void => {
      if (markingGroup) {
        updateMarkingGroup(formValues);
      } else {
        createMarkingGroup(formValues);
      }
    },
    [createMarkingGroup, markingGroup, updateMarkingGroup]
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Name />
        <Description />
        <GroupMembers
          groupMemberType={GroupMemberType.Marker}
          organisationUsers={markerOrganisationUsers}
        />
        {nonMarkerOrganisationUsers.length > 0 && (
          <GroupMembers
            groupMemberType={GroupMemberType.NonMarker}
            organisationUsers={nonMarkerOrganisationUsers}
          />
        )}
        <FormButtonsContainer>
          <SaveButton
            isSaving={
              postGroup.isLoading ||
              patchGroup.isLoading ||
              postGroupUsers.isLoading ||
              deleteGroupUsers.isLoading
            }
          />
        </FormButtonsContainer>
      </form>
    </FormProvider>
  );
}
